import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  CircularProgress,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { GenericDialog } from '@/components/shared/GenericDialog/GenericDialog';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { formatDate, formatDateToYYYYMMDD } from '../../utils';
import {
  LogCategory,
  LogEntriesPerDateFetchStrategy,
  LogTemplateType,
  LogEntriesPerDateDocument,
  useAllLogsTemplatesByTypeQuery,
  useLogEntriesPerDateQuery,
  useUpsertLogEntryMutation,
  GetAggregatedKpiLogEntriesDocument,
  GetLogEntryCardsDocument,
} from '@/__generated__/graphql';
import { COLLECTION_NAMES } from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { useGetPlatformId } from '@/hooks';
import {
  indirectPobDialogOpen,
  indirectPobDialogStructureId,
  kpiPanelSnackbarMessage,
} from '../../state';

const INDIRECT_POB_FIELD_KEY = 'indirect_pob';

export const IndirectPobLogDialog = () => {
  const [open, setOpen] = useAtom(indirectPobDialogOpen);
  const structureId = useAtomValue(indirectPobDialogStructureId);
  const setStructureId = useSetAtom(indirectPobDialogStructureId);
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [value, setValue] = useState<number | ''>('');
  const valueDirtyReference = useRef(false);
  const platformId = useGetPlatformId();

  const onClose = useCallback(() => {
    setOpen(false);
    setStructureId(undefined);
  }, [setOpen, setStructureId]);

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const selectedDateLogKey = useMemo(() => formatDateToYYYYMMDD(selectedDate), [selectedDate]);

  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
    atom: kpiPanelSnackbarMessage,
  });

  const { data: indirectPobTemplateData, loading: templateLoading } =
    useAllLogsTemplatesByTypeQuery({
      variables: {
        input: {
          structureId: structureId ?? '',
          type: LogTemplateType.IndirectPobTemplate,
        },
      },
      fetchPolicy: 'cache-first',
      skip: !open || !structureId,
    });

  const [upsertLogEntry, { loading: isSubmitting }] = useUpsertLogEntryMutation();

  const indirectPobTemplateId = indirectPobTemplateData?.allLogsTemplates?.at(0)?.id;

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    setSelectedDate(new Date());
  }, [open]);

  const { data: indirectPobLogEntriesData, loading: indirectPobLogEntriesLoading } =
    useLogEntriesPerDateQuery({
      variables: {
        input: {
          referenceId: platformId ?? '',
          structureId,
          collectionName: COLLECTION_NAMES.PLATFORM,
          logDate: selectedDateLogKey,
          timezone: userTimezone,
          fetchStrategy: LogEntriesPerDateFetchStrategy.Individual,
          category: LogCategory.IndirectPobLog,
        },
      },
      skip: !open || !structureId || !platformId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (!open) {
      return;
    }
    valueDirtyReference.current = false;
    setValue('');
  }, [open, selectedDate]);

  useEffect(() => {
    if (!open || indirectPobLogEntriesLoading || valueDirtyReference.current) {
      return;
    }

    const row = indirectPobLogEntriesData?.logEntriesPerDate?.find(
      (entry) => entry.fieldKey === INDIRECT_POB_FIELD_KEY
    );

    setValue(row === undefined ? '' : row.total);
  }, [
    indirectPobLogEntriesData?.logEntriesPerDate,
    indirectPobLogEntriesLoading,
    open,
    selectedDateLogKey,
  ]);

  const formattedDate = useMemo(() => {
    return formatDate(selectedDate, '2-digit', 'short', 'numeric');
  }, [selectedDate]);

  const isIndirectPobValueLoading = Boolean(platformId) && indirectPobLogEntriesLoading;

  const canSubmit = useMemo(() => {
    return (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= 0 &&
      !isSubmitting &&
      Boolean(indirectPobTemplateId)
    );
  }, [value, isSubmitting, indirectPobTemplateId]);

  const handleAdd = useCallback(async () => {
    if (!canSubmit || typeof value !== 'number' || !indirectPobTemplateId || !structureId) {
      return;
    }
    try {
      await upsertLogEntry({
        variables: {
          input: {
            logDate: selectedDateLogKey,
            structureId,
            collectionName: COLLECTION_NAMES.PLATFORM,
            ...(platformId ? { referenceId: platformId } : {}),
            category: LogCategory.IndirectPobLog,
            logsTemplateId: indirectPobTemplateId,
            fieldValues: [{ fieldKey: INDIRECT_POB_FIELD_KEY, value }],
            lastModifiedFor: 'Update Indirect POB Log',
          },
        },
        refetchQueries: [
          LogEntriesPerDateDocument,
          GetAggregatedKpiLogEntriesDocument,
          GetLogEntryCardsDocument,
        ],
      });
      showSnackBar('Indirect POB logged successfully', 'success');
      onClose();
    } catch (error) {
      showSnackBar(extractErrorMessageFromUnknownError(error), 'error');
    }
  }, [
    canSubmit,
    indirectPobTemplateId,
    onClose,
    platformId,
    selectedDateLogKey,
    showSnackBar,
    structureId,
    upsertLogEntry,
    value,
  ]);

  const content = (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {open && !templateLoading && !indirectPobTemplateId && (
        <Alert severity="warning">Indirect POB template is not enabled for this deployment.</Alert>
      )}
      <Box>
        <TextField
          fullWidth
          label="Date"
          value={formattedDate}
          onClick={(event) => setDateAnchor(event.currentTarget)}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={(event) => setDateAnchor(event.currentTarget)}>
                    <CalendarTodayOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={() => setDateAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <DateCalendar
            value={selectedDate}
            onChange={(nextDate) => {
              if (nextDate) {
                setSelectedDate(nextDate);
              }
              setDateAnchor(null);
            }}
            disableFuture
          />
        </Popover>
      </Box>

      <TextField
        fullWidth
        label="Indirect-POB"
        type="number"
        value={value}
        disabled={isIndirectPobValueLoading}
        helperText={isIndirectPobValueLoading ? 'Loading existing POB value...' : undefined}
        onChange={(event) => {
          valueDirtyReference.current = true;
          const next = event.target.value;
          if (next === '') {
            setValue('');
            return;
          }
          const numericValue = Number(next);
          setValue(Number.isFinite(numericValue) ? numericValue : '');
        }}
        slotProps={{
          htmlInput: { min: 0, step: 'any' },
          input: {
            endAdornment: isIndirectPobValueLoading ? (
              <InputAdornment position="end" sx={{ marginRight: 0.5 }}>
                <CircularProgress color="inherit" size={20} />
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
    </Stack>
  );

  const actions = (
    <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ px: 2, pb: 1 }}>
      <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 2 }} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={() => {
          void handleAdd();
        }}
        sx={{ ...containedButtonStyle, borderRadius: 2, background: abyssColors.primary[700] }}
        disabled={!canSubmit || templateLoading || isIndirectPobValueLoading}
      >
        {isSubmitting ? <CircularProgress size={16} color="inherit" /> : 'Add'}
      </Button>
    </Stack>
  );

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title="Add Indirect POB"
      content={content}
      actions={actions}
      maxWidth="sm"
      paperStyles={{ borderRadius: 2 }}
      actionsStyles={{ justifyContent: 'flex-end' }}
      id="add-indirect-pob-dialog"
    />
  );
};
